import { Box, Container, HStack, Text } from "@chakra-ui/react";
import Fuse from "fuse.js";
import { groupBy, truncate } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { connectModal, InjectedProps } from "redux-modal";
import { PartialAssetFields } from "src/api/fragments";
import { AssetTypeEnum } from "src/api/generated/types";
import { Modal } from "src/components/Modal";
import { AwakenTooltip, Button } from "src/components/styled";
import { AssetIcon } from "src/components/styled/Assets";
import { Input } from "src/components/styled/Form/Input";
import { hasValue } from "src/core";
import { useClientById } from "src/hooks";
import { useTheme } from "src/hooks/useTheme";
import { colors } from "src/theme";

type Props = InjectedProps & {
  onSelect: (assets: PartialAssetFields[]) => void;
  canOnlySelectOne?: boolean;
  provider?: string;
  defaultSelectedAssets?: PartialAssetFields[];
};

type AssetGroup = {
  assets: PartialAssetWithKey[];
  name: string;
  symbol: string;
  coinGeckoTokenId: string;
  key: string;
};

type PartialAssetWithKey = PartialAssetFields & {
  key: string;
};

const _getAssetKey = (a: PartialAssetFields): string => {
  if (a.coinGeckoTokenId) {
    return a.coinGeckoTokenId;
  }
  if (a.type === AssetTypeEnum.FiatCurrency) {
    return a.symbol || a.id;
  }
  if (a.type === AssetTypeEnum.Nft) {
    return `${a.contractAddress}-${a.tokenId || ""}`;
  }
  if (a.type === AssetTypeEnum.FungibleToken) {
    return a.symbol || a.id;
  }
  return a.id;
};

function _SearchAssetModal({
  handleHide,
  show: isVisible,
  canOnlySelectOne,
  onSelect,
  provider,
  defaultSelectedAssets = [],
}: Props) {
  const { clientId } = useParams<{ clientId: string }>();
  const [search, setSearch] = useState("");
  const theme = useTheme();
  const [selectedAssets, setSelectedAssets] = useState<PartialAssetWithKey[]>(
    defaultSelectedAssets.map((a) => ({ ...a, key: _getAssetKey(a) }))
  );
  const { assets, getAssets } = useClientById(clientId);

  useEffect(() => {
    void getAssets({ fetchPolicy: "cache-first" });
  }, [getAssets]);

  const onHide = () => {
    console.log(selectedAssets);
    onSelect(selectedAssets);
    handleHide();
    setSearch("");
    setSelectedAssets([]);
  };

  const sortedAssets = useMemo((): AssetGroup[] => {
    const sorted = [...assets].sort((a, b) => {
      if (a.coinGeckoTokenId && !b.coinGeckoTokenId) return -1;
      if (!a.coinGeckoTokenId && b.coinGeckoTokenId) return 1;
      return 0;
    });

    // AssetGroup
    const groupedAssets = groupBy(sorted, _getAssetKey);

    const groups = Object.entries(groupedAssets).map(
      ([key, assets]): AssetGroup => ({
        assets: assets.map((a) => ({ ...a, key: key })),
        name: assets[0]?.name || "",
        symbol: assets[0]?.symbol || "",
        coinGeckoTokenId: assets[0]?.coinGeckoTokenId || "",
        key,
      })
    );

    return groups;
  }, [assets]);

  const fuse = useMemo(() => {
    return new Fuse(sortedAssets, {
      keys: [
        {
          name: "name",
          weight: 0.3,
        },
        {
          name: "symbol",
          weight: 0.3,
        },
        {
          name: "coinGeckoTokenId",
          weight: 0.4,
          getFn: (asset) => (asset.coinGeckoTokenId ? "1" : "0"),
        },
      ],
      threshold: 0.3,
      includeScore: true,
      shouldSort: true,
      sortFn: (a, b) => {
        // First sort by score
        if (a.score !== b.score) {
          return a.score - b.score;
        }
        // Then sort by coinGeckoTokenId presence
        const aHasGecko = !!a.item.coinGeckoTokenId;
        const bHasGecko = !!b.item.coinGeckoTokenId;
        if (aHasGecko && !bHasGecko) return -1;
        if (!aHasGecko && bHasGecko) return 1;
        return 0;
      },
    });
  }, [sortedAssets]);

  const searchResults = useMemo(() => {
    if (!search) return sortedAssets.slice(0, 25);
    return (
      fuse
        .search(search, {
          limit: 100,
        })
        .map((result) => result.item) || []
    );
  }, [search, fuse, sortedAssets]);

  const handleAssetClick = (assets: PartialAssetWithKey[]) => {
    if (canOnlySelectOne) {
      onSelect(assets);
      handleHide();
    } else {
      const asset = assets[0];
      if (selectedAssets.find((a) => a.key === asset.key)) {
        setSelectedAssets(selectedAssets.filter((a) => a.key !== asset.key));
      } else {
        setSelectedAssets([...selectedAssets, ...assets]);
      }
    }
  };

  // for rendering
  const uniqSelectedAssets = useMemo(() => {
    // Group assets by key
    const groupedByKey = groupBy(selectedAssets, (a) => a.key);

    // For each group, prioritize assets with images
    return Object.values(groupedByKey).map((assets) => {
      const withImage = assets.find((a) => !!a.iconImageUrl);
      return withImage || assets[0];
    });
  }, [selectedAssets]);

  const submitSelectedAssets = () => {
    onSelect(selectedAssets);
    handleHide();
  };

  const removeAsset = (assetId: string) => {
    setSelectedAssets(selectedAssets.filter((a) => a.id !== assetId));
  };

  return (
    <Modal
      title={
        <HStack>
          <Text
            color={theme.header}
            fontSize="3xl"
            margin={0}
            fontWeight={"bold"}
          >
            Search Assets
          </Text>
        </HStack>
      }
      isVisible={isVisible}
      handleHide={onHide}
      footerProps={{ padding: 0 }}
      Footer={
        <>
          {uniqSelectedAssets.length > 0 && !canOnlySelectOne && (
            <div
              style={{
                width: "100%",
                padding: 10,
                borderTop: `1px solid ${theme.border}`,
              }}
            >
              <Button
                variant="primary"
                style={{
                  width: "100%",
                }}
                onClick={submitSelectedAssets}
              >
                Select Assets
              </Button>
            </div>
          )}
        </>
      }
    >
      <Container padding="0px" marginTop="10px !important">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or symbol..."
        />

        {uniqSelectedAssets.length > 0 && (
          <Box mt={2}>
            <HStack spacing={1} alignItems="flex-start" flexWrap="wrap">
              {uniqSelectedAssets.map((asset) => (
                <Box
                  key={asset.id}
                  bg={theme.secondaryBackground}
                  px={1}
                  py={1}
                  borderRadius="50px"
                  display="flex"
                  alignItems="center"
                >
                  <AssetIcon
                    asset={asset}
                    style={{
                      width: 25,
                      height: 25,
                      marginRight: 5,
                      borderRadius: asset.type === AssetTypeEnum.Nft ? 5 : 100,
                    }}
                  />
                  <Text color={theme.text} fontSize="sm" fontWeight="medium">
                    {asset.type === AssetTypeEnum.Nft
                      ? asset.name
                      : asset.symbol}
                  </Text>
                  <Box
                    ml={3}
                    mr={2}
                    cursor="pointer"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      removeAsset(asset.id);
                    }}
                  >
                    <i
                      className="fas fa-times"
                      style={{ fontSize: "12px", color: theme.text }}
                    />
                  </Box>
                </Box>
              ))}
            </HStack>
          </Box>
        )}

        {searchResults.length > 0 && (
          <Box mt={6}>
            <div style={{ height: 400, overflowY: "auto" }}>
              {searchResults.map((result) => (
                <Box
                  key={result.key}
                  variant="outline"
                  width="100%"
                  mb={3}
                  _hover={{
                    bg: theme.secondaryBackground,
                  }}
                  style={{
                    padding: 12,
                    border: `2px solid ${
                      selectedAssets.find((a) => a.key === result.key)
                        ? colors.positive
                        : theme.border
                    }`,
                    borderRadius: 10,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    handleAssetClick(result.assets);
                  }}
                >
                  <HStack width="100%" spacing={2}>
                    <AssetIcon
                      asset={result.assets[0]}
                      style={{
                        width: 40,
                        borderRadius:
                          result.assets[0].type === AssetTypeEnum.Nft ? 5 : 100,
                        height: 40,
                      }}
                    />
                    <Box flex={1} overflow="hidden">
                      <Text color={theme.header} style={{ textAlign: "left" }}>
                        {result.name}{" "}
                        {result.coinGeckoTokenId && (
                          <AwakenTooltip message="This asset has pricing data on Coingecko.">
                            <i
                              className="fa-sharp fa-check-circle"
                              style={{ color: colors.positive }}
                            />
                          </AwakenTooltip>
                        )}
                      </Text>
                      <AwakenTooltip
                        message={`Assets across ${result.assets
                          .map(
                            (a) =>
                              `${a.provider}${
                                a.contractAddress
                                  ? ` (${a.contractAddress})`
                                  : ""
                              }`
                          )
                          .join(", ")}`}
                      >
                        <Text
                          color={theme.text}
                          fontSize="xs"
                          isTruncated
                          noOfLines={1}
                          style={{
                            textAlign: "left",
                            marginTop: 3,
                            maxWidth: "100%",
                          }}
                        >
                          {result.assets
                            .map((a) => a.contractAddress)
                            .filter(hasValue)
                            .join(", ")}
                        </Text>
                      </AwakenTooltip>
                    </Box>
                    <Text color={theme.text}>
                      {truncate(result.symbol || "", {
                        length: 10,
                      })}
                    </Text>
                  </HStack>
                </Box>
              ))}
            </div>
          </Box>
        )}
      </Container>
    </Modal>
  );
}

export const SearchAssetModal = connectModal({
  name: "SearchAssetModal",
})(_SearchAssetModal);
